<template>
  <div class="osp-panel">
    <div class="osp-header">
      <h2 class="osp-title">Gerenciar Ordens de Serviço</h2>
      <p class="osp-subtitle">Uma OS é um grupo de rotinas — designe várias de uma vez para 1 ou mais responsáveis.</p>
    </div>

    <!-- ── Detalhe de uma OS ── -->
    <div v-if="selected" class="osp-body">
      <button class="osp-back" type="button" @click="selected = null">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        Voltar
      </button>

      <div class="osp-detail-header">
        <div>
          <div class="osp-detail-title">OS{{ selected.osNumber }} — {{ selected.title }}</div>
          <div class="osp-detail-meta">
            {{ selected.projectName }} · {{ selected.responsaveis.join(', ') }}
            <span v-if="selected.dueDate"> · prazo {{ formatDate(selected.dueDate) }}</span>
            <span v-if="selected.requireAll"> · todos precisam preencher</span>
            <span class="osp-priority-badge" :class="'p-' + selected.priority">{{ priorityLabel(selected.priority) }}</span>
            <span class="osp-status-badge" :class="'s-' + selected.status">{{ statusLabel(selected.status) }}</span>
          </div>
        </div>
        <div v-if="accessLevel >= 2" class="osp-item-actions">
          <template v-if="selected.status !== 'cancelled'">
            <button class="osp-btn" type="button" @click="showAddItem = !showAddItem">+ Adicionar rotina</button>
            <button class="osp-btn" type="button" @click="showDesignate = !showDesignate">Designar OS</button>
            <button class="osp-btn osp-btn-danger" type="button" @click="cancelOs">Cancelar OS</button>
          </template>
          <button v-if="accessLevel >= 3" class="osp-btn osp-btn-danger" type="button" @click="deleteOs(selected.id)">Apagar OS</button>
        </div>
      </div>

      <div v-if="lineage.length > 1" class="osp-lineage">
        <div class="osp-section-label">Designações desta OS</div>
        <div v-for="os in lineage" :key="os.id" class="osp-lineage-item" :class="{ current: os.id === selected.id }">
          <span class="osp-item-title">OS{{ os.osNumber }}</span>
          <span class="osp-status-badge" :class="'s-' + os.status">{{ statusLabel(os.status) }}</span>
          <span class="osp-item-desc">{{ os.responsaveis.join(', ') }}</span>
          <div class="osp-lineage-actions">
            <button v-if="os.id !== selected.id" class="osp-btn" type="button" @click="openDetail(os)">Ver</button>
            <button v-if="accessLevel >= 3" class="osp-btn osp-btn-danger" type="button" @click="deleteOs(os.id)">Apagar</button>
          </div>
        </div>
      </div>

      <div v-if="showAddItem" class="osp-new-form">
        <div class="osp-grid">
          <div class="osp-field">
            <label class="osp-label">Título da rotina</label>
            <input class="osp-input" type="text" v-model="newItem.title" placeholder="Ex.: Verificar extintores" />
          </div>
          <div class="osp-field">
            <label class="osp-label">Descrição (opcional)</label>
            <input class="osp-input" type="text" v-model="newItem.description" />
          </div>
        </div>
        <p v-if="addItemError" class="osp-error">{{ addItemError }}</p>
        <div class="osp-actions">
          <button class="osp-btn osp-btn-primary" type="button" :disabled="addingItem" @click="submitAddItem">
            {{ addingItem ? 'Adicionando...' : 'Adicionar rotina' }}
          </button>
        </div>
      </div>

      <div v-if="showDesignate" class="osp-new-form">
        <div class="osp-section-label">Designar esta OS a novo(s) responsável(is)</div>
        <p class="osp-hint">Cria uma OS numerada nova (mesmas rotinas, pendentes de novo) para o(s) responsável(is) escolhido(s), sem apagar o histórico desta.</p>
        <div class="osp-grid">
          <div class="osp-field osp-field-wide">
            <label class="osp-label">Responsáveis</label>
            <select class="osp-select" v-model="designatePick" @change="addDesignateResponsavel">
              <option value="">— Adicionar responsável —</option>
              <option v-for="u in userOptions" :key="u.username" :value="u.username">{{ u.name }}</option>
            </select>
            <div v-if="designateForm.responsavelUserIds.length" class="osp-chips">
              <span v-for="uid in designateForm.responsavelUserIds" :key="uid" class="osp-chip">
                {{ userNameOf(uid) }}
                <button class="osp-chip-x" type="button" @click="removeDesignateResponsavel(uid)">×</button>
              </span>
            </div>
          </div>
          <div class="osp-field">
            <label class="osp-label">Prazo</label>
            <input class="osp-input" type="date" v-model="designateForm.dueDate" />
          </div>
          <div class="osp-field osp-field-wide">
            <label class="osp-check-row">
              <input type="checkbox" v-model="designateForm.requireAll" />
              Todos precisam preencher (cria uma OS separada por pessoa, em vez de compartilhada)
            </label>
          </div>
        </div>
        <p v-if="designateError" class="osp-error">{{ designateError }}</p>
        <p v-if="designateResult" class="osp-success">{{ designateResult }}</p>
        <div class="osp-actions">
          <button class="osp-btn osp-btn-primary" type="button" :disabled="designating" @click="submitDesignate">
            {{ designating ? 'Designando...' : 'Designar OS' }}
          </button>
        </div>
      </div>

      <div class="osp-items">
        <div v-for="item in selectedItems" :key="item.id" class="osp-item-card">
          <div class="osp-item-top">
            <div>
              <div class="osp-item-title">{{ item.title }}</div>
              <div v-if="item.description" class="osp-item-desc">{{ item.description }}</div>
            </div>
            <span class="osp-status-badge" :class="'s-' + item.status">{{ itemStatusLabel(item.status) }}</span>
          </div>

          <div v-if="item.status !== 'pending' || item.completedAt" class="osp-item-evidence">
            <div v-if="item.textResponse" class="osp-evidence-line">{{ item.textResponse }}</div>
            <div v-if="item.checkResponse" class="osp-evidence-line">✓ Marcado como feito</div>
            <img v-if="item.photoResponse" :src="item.photoResponse" class="osp-evidence-photo" alt="Evidência" />
            <div v-if="item.completedByName" class="osp-evidence-meta">Concluído por {{ item.completedByName }} em {{ formatDateTime(item.completedAt) }}</div>
          </div>

          <div v-if="item.reviewNotes && item.status === 'pending'" class="osp-review-note">
            Reprovado por {{ item.reviewedByName }}: {{ item.reviewNotes }}
          </div>

          <div v-if="accessLevel >= 3 && item.status === 'done'" class="osp-item-actions">
            <button class="osp-btn osp-btn-primary" type="button" @click="approve(item.id)">Aprovar</button>
            <button class="osp-btn osp-btn-danger" type="button" @click="openReject(item.id)">Reprovar</button>
          </div>

          <div v-if="rejecting === item.id" class="osp-reject-form">
            <textarea class="osp-textarea" v-model="rejectNotes" rows="2" placeholder="Motivo da reprovação (obrigatório)"></textarea>
            <div class="osp-item-actions">
              <button class="osp-btn" type="button" @click="rejecting = ''">Cancelar</button>
              <button class="osp-btn osp-btn-danger" type="button" :disabled="!rejectNotes.trim()" @click="reject(item.id)">Confirmar reprovação</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Lista + criação ── -->
    <div v-else class="osp-body">
      <div v-if="accessLevel >= 2" class="osp-form">
        <button class="osp-btn osp-btn-primary" type="button" @click="showForm = !showForm">
          {{ showForm ? 'Fechar formulário' : '+ Nova Ordem de Serviço' }}
        </button>

        <div v-if="showForm" class="osp-new-form">
          <div class="osp-grid">
            <div class="osp-field osp-field-wide">
              <label class="osp-label">Título da OS</label>
              <input class="osp-input" type="text" v-model="form.title" placeholder="Ex.: Manutenção elétrica — bloco B" />
            </div>
            <div class="osp-field">
              <label class="osp-label">Projeto</label>
              <select class="osp-select" v-model="form.projectId">
                <option value="">— Selecione —</option>
                <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
            <div class="osp-field">
              <label class="osp-label">Prioridade</label>
              <select class="osp-select" v-model="form.priority">
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div class="osp-field">
              <label class="osp-label">Prazo</label>
              <input class="osp-input" type="date" v-model="form.dueDate" />
            </div>
            <div class="osp-field osp-field-wide">
              <label class="osp-label">Descrição</label>
              <textarea class="osp-textarea" v-model="form.description" rows="2"></textarea>
            </div>
            <div class="osp-field osp-field-wide">
              <label class="osp-label">Responsáveis <span class="osp-hint">— o primeiro que concluir um item já vale para todos</span></label>
              <select class="osp-select" v-model="respPick" @change="addResponsavel">
                <option value="">— Adicionar responsável —</option>
                <option v-for="u in userOptions" :key="u.username" :value="u.username">{{ u.name }}</option>
              </select>
              <div v-if="form.responsavelUserIds.length" class="osp-chips">
                <span v-for="uid in form.responsavelUserIds" :key="uid" class="osp-chip">
                  {{ userNameOf(uid) }}
                  <button class="osp-chip-x" type="button" @click="removeResponsavel(uid)">×</button>
                </span>
              </div>
            </div>
          </div>

          <div class="osp-items-form">
            <div class="osp-section-label">Rotinas desta OS</div>
            <div v-for="(item, idx) in form.items" :key="idx" class="osp-item-row">
              <input class="osp-input" type="text" v-model="item.title" placeholder="Título da rotina" />
              <input class="osp-input" type="text" v-model="item.description" placeholder="Descrição (opcional)" />
              <button class="osp-chip-x" type="button" @click="removeItem(idx)">×</button>
            </div>
            <button class="osp-btn" type="button" @click="addItem">+ Adicionar rotina</button>
          </div>

          <p v-if="error" class="osp-error">{{ error }}</p>
          <div class="osp-actions">
            <button class="osp-btn osp-btn-primary" type="button" :disabled="saving" @click="submit">
              {{ saving ? 'Criando...' : 'Criar OS' }}
            </button>
          </div>
        </div>
      </div>

      <div class="osp-filters">
        <select class="osp-select" v-model="filters.projectId" @change="applyFilters">
          <option value="">Todos os projetos</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <select class="osp-select" v-model="filters.status" @change="applyFilters">
          <option value="">Todos os status</option>
          <option value="pending">Em andamento</option>
          <option value="approved">Aprovadas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <label class="osp-page-size">
          Exibir:
          <select class="osp-select" v-model.number="pageSize" @change="currentPage = 1">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </label>
        <button v-if="accessLevel >= 3" class="osp-btn osp-btn-primary" type="button" @click="exportExcel">Exportar Excel</button>
      </div>

      <div v-if="loading" class="osp-loading">Carregando...</div>
      <div v-else-if="orders.length === 0" class="osp-empty">Nenhuma OS encontrada.</div>
      <template v-else>
        <div class="osp-list">
          <button v-for="os in pagedOrders" :key="os.id" class="osp-os-card" type="button" @click="openDetail(os)">
            <div class="osp-os-card-top">
              <span class="osp-item-title">OS{{ os.osNumber }} — {{ os.title }}</span>
              <span class="osp-status-badge" :class="'s-' + os.status">{{ statusLabel(os.status) }}</span>
            </div>
            <div class="osp-item-desc">{{ os.projectName }} · {{ os.responsaveis.join(', ') }}</div>
            <div class="osp-progress">
              <div class="osp-progress-bar"><div class="osp-progress-fill" :style="{ width: progressPct(os) + '%' }"></div></div>
              <span>{{ os.itemsApproved }}/{{ os.itemsTotal }} itens aprovados</span>
            </div>
          </button>
        </div>
        <div v-if="totalPages > 1" class="osp-pagination">
          <button
            v-for="p in totalPages"
            :key="p"
            class="osp-page-btn"
            :class="{ active: p === currentPage }"
            type="button"
            @click="currentPage = p"
          >{{ p }}</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import * as XLSX from 'xlsx'
import { maintenanceStore, type ServiceOrder, type ServiceOrderDetail, type ServiceOrderExportRow } from '@/store/maintenance'
import { accountsStore } from '@/store/accounts'
import { projectsStore } from '@/store/projects'

export default defineComponent({
  name: 'MaintenanceServiceOrdersPanel',
  props: {
    accessLevel: { type: Number, default: 0 }
  },
  setup() {
    const loading = ref(true)
    const saving = ref(false)
    const error = ref('')
    const showForm = ref(false)
    const respPick = ref('')
    const selected = ref<ServiceOrderDetail | null>(null)
    const lineage = ref<ServiceOrder[]>([])
    const rejecting = ref('')
    const rejectNotes = ref('')

    const showAddItem = ref(false)
    const addingItem = ref(false)
    const addItemError = ref('')
    const newItem = reactive({ title: '', description: '' })

    const showDesignate = ref(false)
    const designating = ref(false)
    const designateError = ref('')
    const designateResult = ref('')
    const designatePick = ref('')
    const designateForm = reactive({ responsavelUserIds: [] as string[], dueDate: '', requireAll: false })

    const pageSize = ref(10)
    const currentPage = ref(1)

    const filters = reactive({ projectId: '', status: '' })

    const form = reactive({
      title: '',
      description: '',
      priority: 'normal' as 'baixa' | 'normal' | 'alta' | 'urgente',
      projectId: '',
      dueDate: '',
      responsavelUserIds: [] as string[],
      items: [{ title: '', description: '' }]
    })

    const orders = computed(() => maintenanceStore.serviceOrders.items)
    const selectedItems = computed(() => selected.value?.items || [])
    const projects = computed(() => projectsStore.all())
    const userOptions = computed(() =>
      accountsStore.all().filter(u => u.name).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    )

    const userNameOf = (username: string) => userOptions.value.find(u => u.username === username)?.name || username
    const addResponsavel = () => {
      if (respPick.value && !form.responsavelUserIds.includes(respPick.value)) {
        form.responsavelUserIds.push(respPick.value)
      }
      respPick.value = ''
    }
    const removeResponsavel = (username: string) => {
      form.responsavelUserIds = form.responsavelUserIds.filter(u => u !== username)
    }

    const addItem = () => { form.items.push({ title: '', description: '' }) }
    const removeItem = (idx: number) => { form.items.splice(idx, 1) }

    const priorityLabel = (p: string) => ({ baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente' }[p] || 'Normal')
    const statusLabel = (s: string) => ({ pending: 'Em andamento', approved: 'Aprovada', cancelled: 'Cancelada' }[s] || s)
    const itemStatusLabel = (s: string) => ({ pending: 'Pendente', done: 'Aguardando revisão', approved: 'Aprovado' }[s] || s)
    const formatDate = (iso: string) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
    const formatDateTime = (iso: string) => iso ? new Date(iso).toLocaleString('pt-BR') : ''
    const progressPct = (os: ServiceOrder) => os.itemsTotal ? Math.round((os.itemsApproved / os.itemsTotal) * 100) : 0

    const totalPages = computed(() => Math.max(1, Math.ceil(orders.value.length / pageSize.value)))
    const pagedOrders = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      return orders.value.slice(start, start + pageSize.value)
    })

    const reload = async () => {
      loading.value = true
      try {
        await maintenanceStore.loadServiceOrders(filters)
      } finally {
        loading.value = false
      }
    }

    const applyFilters = () => {
      currentPage.value = 1
      reload()
    }

    const submit = async () => {
      error.value = ''
      const items = form.items.filter(it => it.title.trim())
      if (!form.title.trim() || !form.projectId) {
        error.value = 'Informe o título e o projeto da OS.'
        return
      }
      if (!form.responsavelUserIds.length) {
        error.value = 'Selecione ao menos um responsável.'
        return
      }
      if (!items.length) {
        error.value = 'Adicione ao menos uma rotina.'
        return
      }
      saving.value = true
      try {
        const project = projects.value.find(p => p.id === form.projectId)
        await maintenanceStore.createServiceOrder({
          projectId: form.projectId,
          projectName: project?.name || '',
          title: form.title.trim(),
          description: form.description.trim(),
          priority: form.priority,
          dueDate: form.dueDate,
          responsavelUserIds: form.responsavelUserIds,
          items
        })
        form.title = ''
        form.description = ''
        form.priority = 'normal'
        form.projectId = ''
        form.dueDate = ''
        form.responsavelUserIds = []
        form.items = [{ title: '', description: '' }]
        showForm.value = false
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Erro ao criar OS.'
      } finally {
        saving.value = false
      }
    }

    const openDetail = async (os: ServiceOrder) => {
      selected.value = await maintenanceStore.loadServiceOrderDetail(os.id)
      lineage.value = await maintenanceStore.loadServiceOrderLineage(os.id)
      showAddItem.value = false
      addItemError.value = ''
      newItem.title = ''
      newItem.description = ''
      showDesignate.value = false
      designateError.value = ''
      designateResult.value = ''
      designateForm.responsavelUserIds = []
      designateForm.dueDate = ''
      designateForm.requireAll = false
    }

    const deleteOs = async (osId: string) => {
      const wasCurrent = selected.value?.id === osId
      await maintenanceStore.deleteServiceOrder(osId)
      if (wasCurrent) {
        selected.value = null
        lineage.value = []
      } else if (selected.value) {
        lineage.value = await maintenanceStore.loadServiceOrderLineage(selected.value.id)
      }
      await reload()
    }

    const submitAddItem = async () => {
      addItemError.value = ''
      if (!newItem.title.trim() || !selected.value) {
        addItemError.value = 'Informe o título da rotina.'
        return
      }
      addingItem.value = true
      try {
        await maintenanceStore.addServiceOrderItem(selected.value.id, { title: newItem.title.trim(), description: newItem.description.trim() })
        selected.value = await maintenanceStore.loadServiceOrderDetail(selected.value.id)
        newItem.title = ''
        newItem.description = ''
        showAddItem.value = false
        await reload()
      } catch (err) {
        addItemError.value = err instanceof Error ? err.message : 'Erro ao adicionar rotina.'
      } finally {
        addingItem.value = false
      }
    }

    const addDesignateResponsavel = () => {
      if (designatePick.value && !designateForm.responsavelUserIds.includes(designatePick.value)) {
        designateForm.responsavelUserIds.push(designatePick.value)
      }
      designatePick.value = ''
    }
    const removeDesignateResponsavel = (username: string) => {
      designateForm.responsavelUserIds = designateForm.responsavelUserIds.filter(u => u !== username)
    }

    const submitDesignate = async () => {
      designateError.value = ''
      designateResult.value = ''
      if (!selected.value) return
      if (!designateForm.responsavelUserIds.length) {
        designateError.value = 'Selecione ao menos um responsável.'
        return
      }
      designating.value = true
      try {
        const result = await maintenanceStore.designateServiceOrder(selected.value.id, {
          responsavelUserIds: designateForm.responsavelUserIds,
          dueDate: designateForm.dueDate,
          requireAll: designateForm.requireAll
        })
        const numbers = (result.osNumbers || []).map(n => `OS${n}`).join(', ')
        designateResult.value = `Criada(s): ${numbers}.`
        designateForm.responsavelUserIds = []
        designateForm.dueDate = ''
        designateForm.requireAll = false
        lineage.value = await maintenanceStore.loadServiceOrderLineage(selected.value.id)
        await reload()
      } catch (err) {
        designateError.value = err instanceof Error ? err.message : 'Erro ao designar OS.'
      } finally {
        designating.value = false
      }
    }

    const cancelOs = async () => {
      if (!selected.value) return
      await maintenanceStore.cancelServiceOrder(selected.value.id)
      selected.value = null
      await reload()
    }

    const approve = async (itemId: string) => {
      await maintenanceStore.reviewServiceOrderItem(itemId, { approve: true })
      if (selected.value) selected.value = await maintenanceStore.loadServiceOrderDetail(selected.value.id)
    }

    const openReject = (itemId: string) => { rejecting.value = itemId; rejectNotes.value = '' }
    const reject = async (itemId: string) => {
      await maintenanceStore.reviewServiceOrderItem(itemId, { approve: false, notes: rejectNotes.value.trim() })
      rejecting.value = ''
      if (selected.value) selected.value = await maintenanceStore.loadServiceOrderDetail(selected.value.id)
    }

    const exportExcel = async () => {
      const rows = await maintenanceStore.fetchServiceOrdersExport(filters)
      const header = ['OS', 'Rotina', 'Projeto', 'Prioridade', 'Status', 'Concluído por', 'Concluído em', 'Revisado por', 'Observação', 'Nº de refações']
      const data = rows.map((r: ServiceOrderExportRow) => [
        r.osTitle, r.itemTitle, r.projectName, r.priority, itemStatusLabel(r.status),
        r.completedByName, r.completedAt ? formatDateTime(r.completedAt) : '', r.reviewedByName, r.reviewNotes, r.reworkCount
      ])
      const workbook = XLSX.utils.book_new()
      const sheet = XLSX.utils.aoa_to_sheet([header, ...data])
      sheet['!cols'] = [{ wch: 26 }, { wch: 26 }, { wch: 22 }, { wch: 10 }, { wch: 16 }, { wch: 22 }, { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 10 }]
      XLSX.utils.book_append_sheet(workbook, sheet, 'Ordens de Servico')
      const content = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
      const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'ordens-de-servico.xlsx'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    }

    onMounted(async () => {
      try {
        if (accountsStore.all().length === 0) await accountsStore.refresh()
        if (projectsStore.all().length === 0) await projectsStore.refresh()
        await reload()
        if (maintenanceStore.state.pendingAction === 'create') {
          showForm.value = true
          maintenanceStore.state.pendingAction = null
        }
      } finally {
        loading.value = false
      }
    })

    return {
      loading, saving, error, showForm, respPick, selected, selectedItems, lineage, rejecting, rejectNotes,
      showAddItem, addingItem, addItemError, newItem,
      showDesignate, designating, designateError, designateResult, designatePick, designateForm,
      pageSize, currentPage, totalPages, pagedOrders,
      filters, form, orders, projects, userOptions,
      userNameOf, addResponsavel, removeResponsavel, addItem, removeItem,
      priorityLabel, statusLabel, itemStatusLabel, formatDate, formatDateTime, progressPct,
      reload, applyFilters, submit, openDetail, cancelOs, deleteOs, approve, openReject, reject, exportExcel,
      submitAddItem, addDesignateResponsavel, removeDesignateResponsavel, submitDesignate
    }
  }
})
</script>

<style scoped lang="scss">
.osp-panel { display: flex; flex-direction: column; width: 100%; height: 100%; overflow: hidden; }
.osp-header { padding: 18px 22px 0; display: flex; flex-direction: column; gap: 4px; }
.osp-title { font-size: 20px; font-weight: 700; color: var(--ink); margin: 0; }
.osp-subtitle { font-size: 13px; color: var(--ink-muted); margin: 0; }
.osp-body { flex: 1; overflow-y: auto; padding: 16px 22px 24px; display: flex; flex-direction: column; gap: 16px; }

.osp-back { display: flex; align-items: center; gap: 4px; background: none; border: none; color: var(--ink-muted); font-size: 13px; cursor: pointer; padding: 4px 6px; align-self: flex-start; }

.osp-form { display: flex; flex-direction: column; gap: 12px; }
.osp-new-form {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.osp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
.osp-field { display: flex; flex-direction: column; gap: 4px; }
.osp-field-wide { grid-column: 1 / -1; }
.osp-label { font-size: 12px; font-weight: 600; color: var(--ink-muted); }
.osp-hint { font-weight: 400; opacity: .8; }

.osp-input, .osp-select, .osp-textarea {
  border: 1px solid rgba(31,30,26,.18);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--ink);
  background: var(--bg, #fff);
  font-family: inherit;
}
.osp-textarea { resize: vertical; }

.osp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.osp-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(31,30,26,.06);
  border-radius: 999px;
  padding: 4px 6px 4px 12px;
  font-size: 12px;
}
.osp-chip-x { border: none; background: none; cursor: pointer; color: var(--ink-muted); font-size: 14px; line-height: 1; padding: 2px 4px; }

.osp-items-form { display: flex; flex-direction: column; gap: 8px; }
.osp-section-label { font-size: 13px; font-weight: 700; color: var(--ink); }
.osp-item-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; align-items: center; }

.osp-error { color: #dc2626; font-size: 12px; margin: 0; }
.osp-actions { display: flex; justify-content: flex-end; }

.osp-btn {
  border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  background: rgba(31,30,26,.08); color: var(--ink);
  &:disabled { opacity: .6; cursor: default; }
}
.osp-btn-primary { background: var(--accent); color: #fff; }
.osp-btn-danger { background: rgba(220,38,38,.1); color: #dc2626; }

.osp-filters { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.osp-page-size { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink-muted); }
.osp-check-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink); }
.osp-success { color: #16a34a; font-size: 12px; margin: 0; }

.osp-loading, .osp-empty { font-size: 13px; color: var(--ink-muted); padding: 24px 0; text-align: center; }

.osp-pagination { display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; padding-top: 4px; }
.osp-page-btn {
  min-width: 32px; height: 32px; padding: 0 8px;
  border: 1px solid rgba(31,30,26,.15); border-radius: 8px;
  background: var(--card, #fff); color: var(--ink); font-size: 13px; font-weight: 600; cursor: pointer;
  &.active { background: var(--accent); color: #fff; border-color: var(--accent); }
}

.osp-list { display: flex; flex-direction: column; gap: 10px; }
.osp-os-card {
  text-align: left;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  display: flex; flex-direction: column; gap: 6px;
  &:hover { border-color: var(--accent); }
}
.osp-os-card-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }

.osp-item-title { font-size: 13px; font-weight: 700; color: var(--ink); }
.osp-item-desc { font-size: 12px; color: var(--ink-muted); }

.osp-progress { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--ink-muted); }
.osp-progress-bar { flex: 1; height: 6px; border-radius: 999px; background: rgba(31,30,26,.08); overflow: hidden; }
.osp-progress-fill { height: 100%; background: var(--accent); }

.osp-priority-badge, .osp-status-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: 6px;
}
.osp-priority-badge {
  &.p-baixa { background: rgba(100,116,139,.12); color: #475569; }
  &.p-normal { background: rgba(37,99,235,.12); color: #2563eb; }
  &.p-alta { background: rgba(217,119,6,.12); color: #d97706; }
  &.p-urgente { background: rgba(220,38,38,.12); color: #dc2626; }
}
.osp-status-badge {
  margin-left: 0;
  &.s-pending { background: rgba(217,119,6,.12); color: #d97706; }
  &.s-done { background: rgba(37,99,235,.12); color: #2563eb; }
  &.s-approved { background: rgba(22,163,74,.12); color: #16a34a; }
  &.s-cancelled { background: rgba(100,116,139,.12); color: #475569; }
}

.osp-detail-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.osp-detail-title { font-size: 16px; font-weight: 700; color: var(--ink); }
.osp-detail-meta { font-size: 12px; color: var(--ink-muted); display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 4px; }

.osp-lineage {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.osp-lineage-item {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 6px 0;
  border-bottom: 1px solid rgba(31,30,26,.06);
  &:last-child { border-bottom: none; }
  &.current { font-weight: 700; }
}
.osp-lineage-actions { display: flex; gap: 6px; margin-left: auto; }

.osp-items { display: flex; flex-direction: column; gap: 10px; }
.osp-item-card {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.osp-item-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }

.osp-item-evidence { background: rgba(31,30,26,.03); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 6px; font-size: 12px; }
.osp-evidence-line { color: var(--ink); }
.osp-evidence-meta { color: var(--ink-muted); }
.osp-evidence-photo { max-width: 200px; border-radius: 8px; }

.osp-review-note { background: rgba(220,38,38,.08); color: #b91c1c; border-radius: 8px; padding: 8px 10px; font-size: 12px; }

.osp-item-actions { display: flex; gap: 8px; }
.osp-reject-form { display: flex; flex-direction: column; gap: 8px; }
</style>
